/**
 * Custom bundle builder: multi-step Swiper selection, tiered discounts, Biscuits bundle URL handoff or app bridge.
 */

/** Theme sticky stack (header / announcement / product bar) offset for scrolling `.custom-bundle-builder__inner` into view. */
function cbbReadThemeHeaderStackPx() {
  if (typeof document === "undefined") return 0;
  const st = getComputedStyle(document.documentElement);
  const num = (name) => {
    const raw = st.getPropertyValue(name).trim();
    const v = parseFloat(raw);
    return Number.isFinite(v) ? v : 0;
  };
  return Math.max(
    0,
    num("--header-height") * num("--enable-sticky-header") +
    num("--announcement-bar-height") * num("--enable-sticky-announcement-bar") +
    num("--product-sticky-bar-height")
  );
}

if (!customElements.get("custom-bundle-builder")) {
  customElements.define(
    "custom-bundle-builder",
    class CustomBundleBuilder extends HTMLElement {
      #config = null;
      /** @type {Map<string, { priceCents: number; stepIndex: number; quantity: number; imageUrl?: string; imageAlt?: string }>} */
      #selection = new Map();
      /** After adding a bundle unit, scroll the tier strip to the last filled tile. */
      #tierScrollPending = false;
      /** @type {Map<number, Map<string, string>>} Per-step topic filter selection by group key */
      #topicFilterGroupsByStep = new Map();
      /** @type {Map<number, object | null>} */
      #swipers = new Map();

      /** @type {number} */
      #currentStep = 1;
      /** Highest step index the user has opened (via Continue or stepper); allows returning after jumping back. */
      #furthestVisitedStep = 1;

      #onClickBound = null;
      #onKeyBound = null;
      /** @type {MutationObserver | null} */
      #biscuitsMountObserver = null;
      #onBiscuitsFormReadyBound = null;
      /** @type {MutationObserver | null} */
      #biscuitsSubmitAtcObserver = null;
      /** @type {ReturnType<typeof setTimeout> | null} */
      #biscuitsSubmitSafetyTimer = null;
      /** @type {ReturnType<typeof setTimeout> | null} */
      #biscuitsSubmitFallbackTimer = null;

      /** @type {MediaQueryList | null} */
      #narrowLayoutMq = null;
      #onNarrowLayoutMqChangeBound = null;

      /** @type {(() => void) | null} */
      #onWindowScrollOrResizeBound = null;
      /** @type {number | null} */
      #introSlotStuckRaf = null;

      /** @type {ReturnType<typeof setTimeout> | null} */
      #selectionPersistTimer = null;
      /** After sessionStorage rehydrate, sync selection into Biscuits once hosts exist */
      #restoredSelectionNeedsBiscuitsSync = false;

      constructor() {
        super();
        this.#onClickBound = this.#onClick.bind(this);
        this.#onKeyBound = this.#onKeyDown.bind(this);
        this.#onBiscuitsFormReadyBound = this.#onBiscuitsFormReady.bind(this);
        this.#onNarrowLayoutMqChangeBound = () => {
          this.#onNarrowBundleLayoutChange();
        };
        this.#onWindowScrollOrResizeBound = () => {
          this.#scheduleIntroActionsSlotStuckUpdate();
        };
      }

      connectedCallback() {
        this.#config = this.#readConfig();

        if (!this.#config || this.#config.stepMinPicks.length === 0) {
          const cta = this.querySelector("[data-cbb-primary-cta]");
          if (cta) cta.setAttribute("disabled", "");
          if (this.#biscuitsBridgeActive()) {
            this.#dismissAppLoader();
          }
          return;
        }

        this.addEventListener("click", this.#onClickBound);
        this.addEventListener("keydown", this.#onKeyBound);

        this.#dockIntroActionsUnderSummary();

        this.#loadPersistedSelection();
        this.#applyBundleQueryParamPrefill();

        this.#scheduleInitSwiper(this.#currentStep);
        this.#refreshUI();

        if (this.#biscuitsBridgeActive()) {
          this.#attachBiscuitsBridge();
        }

        if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
          this.#narrowLayoutMq = window.matchMedia("(max-width: 989px)");
          try {
            this.#narrowLayoutMq.addEventListener("change", this.#onNarrowLayoutMqChangeBound);
          } catch (e) {
            this.#narrowLayoutMq.addListener?.(this.#onNarrowLayoutMqChangeBound);
          }
        }

        this.#bindIntroActionsSlotStuckTracking();
      }

      disconnectedCallback() {
        if (this.#narrowLayoutMq && this.#onNarrowLayoutMqChangeBound) {
          try {
            this.#narrowLayoutMq.removeEventListener("change", this.#onNarrowLayoutMqChangeBound);
          } catch (e) {
            this.#narrowLayoutMq.removeListener?.(this.#onNarrowLayoutMqChangeBound);
          }
          this.#narrowLayoutMq = null;
        }

        this.#unbindIntroActionsSlotStuckTracking();

        this.#dockIntroActionsUnderSummary(true);
        this.removeEventListener("click", this.#onClickBound);
        this.removeEventListener("keydown", this.#onKeyBound);
        document.removeEventListener("BiscuitsBundleForm:ready", this.#onBiscuitsFormReadyBound);
        if (this.#biscuitsMountObserver) {
          this.#biscuitsMountObserver.disconnect();
          this.#biscuitsMountObserver = null;
        }
        this.#clearBiscuitsSubmitLoadingUi();
        this.#destroySwipers();

        if (this.#selectionPersistTimer != null) {
          clearTimeout(this.#selectionPersistTimer);
          this.#selectionPersistTimer = null;
        }
      }

      #isNarrowBundleLayout() {
        return (
          typeof window !== "undefined" &&
          typeof window.matchMedia === "function" &&
          window.matchMedia("(max-width: 989px)").matches
        );
      }

      #prefersReducedMotion() {
        return (
          typeof window !== "undefined" &&
          typeof window.matchMedia === "function" &&
          window.matchMedia("(prefers-reduced-motion: reduce)").matches
        );
      }

      /**
       * Scroll viewport so `.custom-bundle-builder__inner` sits below the sticky header stack (parity with bundle builder v2).
       */
      #scrollBuilderInnerIntoView() {
        const inner = this.querySelector(".custom-bundle-builder__inner");
        if (!(inner instanceof HTMLElement) || typeof window === "undefined") return;

        const offset = cbbReadThemeHeaderStackPx();
        const behavior = this.#prefersReducedMotion() ? "auto" : "smooth";
        const apply = () => {
          const y =
            inner.getBoundingClientRect().top + (window.scrollY || window.pageYOffset || 0) - offset;
          try {
            window.scrollTo({ top: Math.max(0, y), behavior });
          } catch (e) {
            window.scrollTo(0, Math.max(0, y));
          }
        };

        if (this.#isNarrowBundleLayout()) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              requestAnimationFrame(apply);
            });
          });
        } else {
          requestAnimationFrame(() => {
            requestAnimationFrame(apply);
          });
        }
      }

      /** Stacked sidebar + mobile swiper placement (≤989px ↔ theme `.custom-bundle-builder__layout` at min-width 990px). */
      #onNarrowBundleLayoutChange() {
        this.#dockIntroActionsUnderSummary(false);
        this.#resetMobileSummaryDrawer();
        this.#scheduleIntroActionsSlotStuckUpdate();
        const step = this.#currentStep;
        this.#destroySwipers();
        this.#applyStepperLayout();
        this.#scheduleInitSwiper(step);
      }

      /** @param {boolean} [disconnectCleanup] When disconnecting CE, restore CTA markup into intro column unconditionally. */
      #dockIntroActionsUnderSummary(disconnectCleanup = false) {
        const introCol = this.querySelector(".custom-bundle-builder__column--intro");
        const slot = this.querySelector("[data-cbb-intro-actions-slot]");
        if (!(introCol instanceof HTMLElement) || !(slot instanceof HTMLElement)) return;

        const actionsEl =
          introCol.querySelector(".custom-bundle-builder__intro-actions") || slot.querySelector(".custom-bundle-builder__intro-actions");

        if (!(actionsEl instanceof HTMLElement)) return;

        if (disconnectCleanup) {
          introCol.append(actionsEl);
          this.#scheduleIntroActionsSlotStuckUpdate();
          return;
        }

        if (this.#isNarrowBundleLayout()) {
          slot.append(actionsEl);
          this.#scheduleIntroActionsSlotStuckUpdate();
          return;
        }

        introCol.append(actionsEl);
        this.#scheduleIntroActionsSlotStuckUpdate();
      }

      #bindIntroActionsSlotStuckTracking() {
        if (typeof window === "undefined" || !this.#onWindowScrollOrResizeBound) return;
        window.addEventListener("scroll", this.#onWindowScrollOrResizeBound, { passive: true });
        window.addEventListener("resize", this.#onWindowScrollOrResizeBound);
        this.#scheduleIntroActionsSlotStuckUpdate();
      }

      #unbindIntroActionsSlotStuckTracking() {
        if (typeof window !== "undefined" && this.#onWindowScrollOrResizeBound) {
          window.removeEventListener("scroll", this.#onWindowScrollOrResizeBound);
          window.removeEventListener("resize", this.#onWindowScrollOrResizeBound);
        }
        if (this.#introSlotStuckRaf != null) {
          cancelAnimationFrame(this.#introSlotStuckRaf);
          this.#introSlotStuckRaf = null;
        }
        const slot = this.querySelector("[data-cbb-intro-actions-slot]");
        if (slot instanceof HTMLElement) {
          slot.classList.remove("custom-bundle-builder__intro-actions-slot--stuck");
        }
      }

      #scheduleIntroActionsSlotStuckUpdate() {
        if (this.#introSlotStuckRaf != null) return;
        this.#introSlotStuckRaf = requestAnimationFrame(() => {
          this.#introSlotStuckRaf = null;
          this.#syncPrimarySummaryViewportOverlap();
          this.#syncIntroActionsSlotStuckClass();
        });
      }

      /**
       * Mobile: slot uses `position: sticky; bottom`. Toggle BEM modifier when the bar is pinned to the viewport.
       */
      #syncIntroActionsSlotStuckClass() {
        const slot = this.querySelector("[data-cbb-intro-actions-slot]");
        if (!(slot instanceof HTMLElement)) return;

        const STUCK_CLASS = "custom-bundle-builder__intro-actions-slot--stuck";

        if (!this.#isNarrowBundleLayout()) {
          slot.classList.remove(STUCK_CLASS);
          return;
        }

        if (!slot.querySelector(".custom-bundle-builder__intro-actions")) {
          slot.classList.remove(STUCK_CLASS);
          return;
        }

        const pos = getComputedStyle(slot).position;
        if (pos !== "sticky") {
          slot.classList.remove(STUCK_CLASS);
          return;
        }

        const rect = slot.getBoundingClientRect();
        const bottomInset = parseFloat(getComputedStyle(slot).bottom);
        const insetPx = Number.isFinite(bottomInset) ? bottomInset : 0;
        const pinLine = window.innerHeight - insetPx;
        const stuck = Math.abs(rect.bottom - pinLine) < 3;

        slot.classList.toggle(STUCK_CLASS, stuck);
      }

      /** @param {Element | null | undefined} panel */
      #dockSwiperPaginationInPanel(panel) {
        if (!(panel instanceof Element)) return;
        const paginationEl = panel.querySelector("[data-cbb-swiper-pagination]");
        const desktopHost = panel.querySelector("[data-cbb-swiper-pagination-host-desktop]");
        const mobileHost = panel.querySelector("[data-cbb-swiper-pagination-host-mobile]");
        if (!(paginationEl instanceof HTMLElement)) return;

        const target =
          desktopHost instanceof HTMLElement && mobileHost instanceof HTMLElement
            ? this.#isNarrowBundleLayout()
              ? mobileHost
              : desktopHost
            : desktopHost instanceof HTMLElement
              ? desktopHost
              : mobileHost;

        if (!(target instanceof HTMLElement)) return;
        target.appendChild(paginationEl);

        if (desktopHost instanceof HTMLElement && mobileHost instanceof HTMLElement) {
          const narrow = this.#isNarrowBundleLayout();

          desktopHost.hidden = narrow;
          desktopHost.setAttribute("aria-hidden", narrow ? "true" : "false");

          mobileHost.hidden = !narrow;
          mobileHost.setAttribute("aria-hidden", narrow ? "false" : "true");
        }
      }

      #biscuitsBridgeActive() {
        return String(this.dataset.biscuitsBridge || "").toLowerCase() === "true";
      }

      #qtySelectorAlwaysVisible() {
        return String(this.dataset.qtyAlwaysVisible || "").toLowerCase() === "true";
      }

      #getMobileSummaryLabels() {
        return {
          show: (this.dataset.mobileSummaryShowLabel || "").trim() || "Bundle summary",
          hide: (this.dataset.mobileSummaryHideLabel || "").trim() || "Hide",
        };
      }

      /** Collapses the sticky mobile summary drawer (breakpoint / Theme Editor safe). */
      #resetMobileSummaryDrawer() {
        const wrap = this.querySelector("[data-cbb-mobile-summary]");
        if (!(wrap instanceof HTMLElement)) return;

        wrap.classList.remove("custom-bundle-builder__mobile-summary--open");

        const btn = wrap.querySelector("[data-cbb-mobile-summary-toggle]");
        const panel = wrap.querySelector("[data-cbb-mobile-summary-panel]");
        const labelEl = wrap.querySelector("[data-cbb-mobile-summary-toggle-label]");
        const { show } = this.#getMobileSummaryLabels();

        if (btn instanceof HTMLButtonElement) {
          btn.setAttribute("aria-expanded", "false");
        }
        if (panel instanceof HTMLElement) {
          panel.setAttribute("aria-hidden", "true");
        }
        if (labelEl instanceof HTMLElement) {
          labelEl.textContent = show;
        }
      }

      /** @param {HTMLButtonElement} btn */
      #toggleMobileSummaryDrawer(btn) {
        const wrap = btn.closest("[data-cbb-mobile-summary]");
        if (!(wrap instanceof HTMLElement)) return;

        const panel = wrap.querySelector("[data-cbb-mobile-summary-panel]");
        const labelEl = wrap.querySelector("[data-cbb-mobile-summary-toggle-label]");
        const { show, hide } = this.#getMobileSummaryLabels();

        const willOpen = !wrap.classList.contains("custom-bundle-builder__mobile-summary--open");
        wrap.classList.toggle("custom-bundle-builder__mobile-summary--open", willOpen);

        btn.setAttribute("aria-expanded", willOpen ? "true" : "false");

        if (panel instanceof HTMLElement) {
          panel.setAttribute("aria-hidden", willOpen ? "false" : "true");
        }
        if (labelEl instanceof HTMLElement) {
          labelEl.textContent = willOpen ? hide : show;
        }

        this.#scheduleIntroActionsSlotStuckUpdate();
      }

      /**
       * Narrow layout: treat the viewport below the sticky slot top edge as obscured so we only hide the
       * duplicate summary when the primary summary is readable above the floating bar.
       */
      #syncPrimarySummaryViewportOverlap() {
        const mobile = this.querySelector("[data-cbb-mobile-summary]");
        const primary = this.querySelector("[data-cbb-summary-primary]");
        const slot = this.querySelector("[data-cbb-intro-actions-slot]");

        if (!(mobile instanceof HTMLElement) || !(primary instanceof HTMLElement)) return;

        if (!this.#isNarrowBundleLayout()) {
          mobile.classList.remove("custom-bundle-builder__mobile-summary--primary-in-view");
          return;
        }

        if (!(slot instanceof HTMLElement)) {
          mobile.classList.remove("custom-bundle-builder__mobile-summary--primary-in-view");
          return;
        }

        const pr = primary.getBoundingClientRect();
        const sr = slot.getBoundingClientRect();
        const vh = typeof window !== "undefined" ? window.innerHeight : 0;

        let readableBottom = vh;
        if (sr.top > 0 && sr.top <= vh) {
          readableBottom = sr.top;
        }

        const BUFFER = 2;
        const overlapsReadable =
          pr.height > 0 && pr.bottom > BUFFER && pr.top < readableBottom - BUFFER;

        this.#syncMobileSummaryHiddenForPrimarySummary(overlapsReadable);
      }

      /** Narrow layout only: hide sticky mobile summary while the primary footer summary is on screen. */
      #syncMobileSummaryHiddenForPrimarySummary(isPrimaryVisible) {
        const mobile = this.querySelector("[data-cbb-mobile-summary]");
        if (!(mobile instanceof HTMLElement)) return;

        const CLASS = "custom-bundle-builder__mobile-summary--primary-in-view";

        if (isPrimaryVisible) {
          this.#resetMobileSummaryDrawer();
          mobile.classList.add(CLASS);
        } else {
          mobile.classList.remove(CLASS);
        }
      }

      #getSectionRoot() {
        return this.closest(".section-custom-bundle-builder-root");
      }

      #sectionHasBiscuitsStepProducts(root) {
        return Boolean(root && root.querySelector(".biscuits-bundle-step__products"));
      }

      #dismissAppLoader() {
        const loader = this.querySelector("[data-cbb-app-loader]");
        if (loader instanceof HTMLElement) {
          loader.hidden = true;
          loader.setAttribute("aria-hidden", "true");
        }

        const inner = this.querySelector(".custom-bundle-builder__inner");
        if (inner instanceof HTMLElement) {
          inner.style.removeProperty("visibility");
          inner.removeAttribute("data-cbb-biscuits-boot");
        }
      }

      #tryDismissLoaderForBiscuitsReady() {
        const root = this.#getSectionRoot();
        if (!this.#sectionHasBiscuitsStepProducts(root)) return;
        this.#dismissAppLoader();
        document.removeEventListener("BiscuitsBundleForm:ready", this.#onBiscuitsFormReadyBound);
        if (this.#biscuitsMountObserver) {
          this.#biscuitsMountObserver.disconnect();
          this.#biscuitsMountObserver = null;
        }
        this.#syncRestoredSelectionToBiscuitsWhenReady();
      }

      #attachBiscuitsBridge() {
        const root = this.#getSectionRoot();
        if (!root) return;

        this.#tryDismissLoaderForBiscuitsReady();
        if (this.#sectionHasBiscuitsStepProducts(root)) return;

        document.addEventListener("BiscuitsBundleForm:ready", this.#onBiscuitsFormReadyBound);

        this.#biscuitsMountObserver = new MutationObserver(() => {
          this.#tryDismissLoaderForBiscuitsReady();
        });
        this.#biscuitsMountObserver.observe(root, { childList: true, subtree: true });
      }

      /** @param {Event} ev */
      #onBiscuitsFormReady(ev) {
        if (!this.#biscuitsBridgeActive()) return;
        const root = this.#getSectionRoot();
        const t = ev.target;
        if (!(t instanceof Node) || !root || !root.contains(t)) return;
        this.#tryDismissLoaderForBiscuitsReady();
      }

      /** @param {Element} el */
      #closestBiscuitsSelectableHost(el) {
        if (!(el instanceof Element)) return null;
        const variantHost = el.closest("biscuits-bundle-variant");
        if (variantHost instanceof Element) return variantHost;
        const productHost = el.closest("biscuits-bundle-product");
        if (productHost instanceof Element) return productHost;
        const tag = el.tagName.toLowerCase();
        if (tag === "biscuits-bundle-variant" || tag === "biscuits-bundle-product") return el;
        return null;
      }

      /** @param {string} variantId */
      #findBiscuitsProductHostForVariant(variantId) {
        const root = this.#getSectionRoot();
        if (!root) return null;
        const want = String(variantId).trim();

        const candidates = root.querySelectorAll("[data-biscuits-selected-variant-id]");
        for (const el of candidates) {
          if (!(el instanceof Element)) continue;
          if (el.getAttribute("data-biscuits-selected-variant-id") !== want) continue;
          const host = this.#closestBiscuitsSelectableHost(el);
          if (host instanceof Element) return host;
        }

        return null;
      }

      /** @param {HTMLElement} card */
      #bridgeSyncBiscuitsProduct(card) {
        if (!this.#biscuitsBridgeActive()) return;
        const variantId = card.getAttribute("data-variant-id");
        if (!variantId) return;
        const host = this.#findBiscuitsProductHostForVariant(variantId);
        if (!(host instanceof HTMLElement)) return;
        host.click();
      }

      #findCbbThemeAddToCartButton() {
        const root = this.#getSectionRoot();
        if (!root) return null;
        const btn = root.querySelector("#AddToCart");
        return btn instanceof HTMLElement ? btn : null;
      }

      /** @returns {string} */
      #findBiscuitsFormErrorMessage() {
        const root = this.#getSectionRoot();
        if (!root) return "";
        const nodes = root.querySelectorAll(".biscuits-bundle-form__messages--error");
        for (const el of nodes) {
          if (!(el instanceof HTMLElement)) continue;
          const text = (el.textContent || "").replace(/\s+/g, " ").trim();
          if (text.length > 0) return text;
        }
        return "";
      }

      #clearBiscuitsSubmitLoadingUi() {
        if (this.#biscuitsSubmitSafetyTimer != null) {
          clearTimeout(this.#biscuitsSubmitSafetyTimer);
          this.#biscuitsSubmitSafetyTimer = null;
        }
        if (this.#biscuitsSubmitFallbackTimer != null) {
          clearTimeout(this.#biscuitsSubmitFallbackTimer);
          this.#biscuitsSubmitFallbackTimer = null;
        }
        if (this.#biscuitsSubmitAtcObserver) {
          this.#biscuitsSubmitAtcObserver.disconnect();
          this.#biscuitsSubmitAtcObserver = null;
        }

        const cta = this.querySelector("[data-cbb-primary-cta]");
        if (cta instanceof HTMLElement && cta.getAttribute("aria-busy") === "true") {
          cta.removeAttribute("aria-busy");
        }
      }

      /** @param {HTMLElement} atc */
      #delegateToBiscuitsAddToCart(atc) {
        this.#clearBiscuitsSubmitLoadingUi();

        const cta = this.querySelector("[data-cbb-primary-cta]");

        if (cta instanceof HTMLElement) {
          cta.setAttribute("aria-busy", "true");
        }

        let sawBusy = false;

        this.#biscuitsSubmitFallbackTimer = window.setTimeout(() => {
          if (!sawBusy) {
            this.#clearBiscuitsSubmitLoadingUi();
          }
        }, 1200);

        this.#biscuitsSubmitAtcObserver = new MutationObserver(() => {
          const busy = atc.getAttribute("aria-busy") === "true";

          if (busy) {
            sawBusy = true;
            if (this.#biscuitsSubmitFallbackTimer != null) {
              clearTimeout(this.#biscuitsSubmitFallbackTimer);
              this.#biscuitsSubmitFallbackTimer = null;
            }
            return;
          }

          if (sawBusy) {
            this.#clearBiscuitsSubmitLoadingUi();
          }
        });

        this.#biscuitsSubmitAtcObserver.observe(atc, {
          attributes: true,
          attributeFilter: ["aria-busy", "disabled"],
        });

        this.#biscuitsSubmitSafetyTimer = window.setTimeout(() => {
          this.#clearBiscuitsSubmitLoadingUi();
        }, 60000);

        atc.click();
      }

      #readConfig() {
        const id = this.dataset.sectionId;
        const el = id ? this.querySelector(`#CustomBundleBuilderConfig-${id}`) : this.querySelector("script[id^='CustomBundleBuilderConfig-']");

        if (!el || !el.textContent) return null;

        try {
          const parsed = JSON.parse(el.textContent);
          parsed.discountTiers = [...(parsed.discountTiers || [])].sort((a, b) => (a.threshold || 0) - (b.threshold || 0));
          return parsed;
        } catch (e) {
          console.warn("CustomBundleBuilder: invalid JSON config", e);
          return null;
        }
      }

      #totalSteps() {
        return this.#config.stepMinPicks.length;
      }

      #rememberSelectionEnabled() {
        return String(this.dataset.rememberSelection || "") === "true";
      }

      #continueUsesNextStepTitle() {
        return String(this.dataset.continueUseNextStepTitle || "") === "true";
      }

      #selectionStorageKey() {
        const sid = (this.dataset.sectionId || "").trim();
        return sid !== "" ? `cbbBundleSelection:${sid}` : "cbbBundleSelection:default";
      }

      #schedulePersistSelectionToStorage() {
        if (!this.#rememberSelectionEnabled()) return;
        if (this.#selectionPersistTimer != null) {
          clearTimeout(this.#selectionPersistTimer);
        }
        this.#selectionPersistTimer = window.setTimeout(() => {
          this.#selectionPersistTimer = null;
          this.#writeSelectionToStorage();
        }, 250);
      }

      #writeSelectionToStorage() {
        if (!this.#rememberSelectionEnabled()) return;
        const key = this.#selectionStorageKey();
        const store = typeof window !== "undefined" ? window.sessionStorage : null;
        if (!store) return;
        try {
          if (this.#selection.size === 0) {
            store.removeItem(key);
            try {
              window.localStorage?.removeItem(key);
            } catch (e) {
              /* ignore */
            }
            return;
          }
          /** @type {Array<[string, { stepIndex: number; quantity: number }]>} */
          const entries = [];
          for (const [id, row] of this.#selection) {
            const q = Number.isFinite(row.quantity) && row.quantity > 0 ? row.quantity : 1;
            entries.push([id, { stepIndex: row.stepIndex, quantity: q }]);
          }
          store.setItem(
            key,
            JSON.stringify({ v: 2, furthestVisitedStep: this.#furthestVisitedStep, entries }),
          );
        } catch (e) {
          /* sessionStorage unavailable (private mode, quota, etc.) */
        }
      }

      #loadPersistedSelection() {
        if (!this.#rememberSelectionEnabled()) return;

        const key = this.#selectionStorageKey();

        try {
          window.localStorage?.removeItem(key);
        } catch (e) {
          /* ignore */
        }

        const store = typeof window !== "undefined" ? window.sessionStorage : null;
        if (!store) return;

        /** @type {string | null} */
        let raw;
        try {
          raw = store.getItem(key);
        } catch (e) {
          return;
        }
        if (!raw) return;

        /** @type {unknown} */
        let parsed;
        try {
          parsed = JSON.parse(raw);
        } catch (e) {
          return;
        }

        if (!parsed || typeof parsed !== "object" || !("entries" in parsed) || !Array.isArray(/** @type {{ entries: unknown }} */ (parsed).entries)) {
          return;
        }
        const vRaw = /** @type {{ v?: unknown }} */ (parsed).v;
        if (vRaw !== 1 && vRaw !== 2) return;

        /** @type {1 | 2} */
        const storageSchema = vRaw;

        let restoredAny = false;

        for (const row of /** @type {{ entries: unknown[] }} */ (parsed).entries) {
          if (!Array.isArray(row) || row.length < 2) continue;
          const variantId = String(row[0] ?? "").trim();
          const meta = row[1];
          if (!variantId || !meta || typeof meta !== "object") continue;
          const savedQty = Number(/** @type {{ quantity?: unknown }} */ (meta).quantity);
          const qty = Number.isFinite(savedQty) && savedQty > 0 ? Math.floor(savedQty) : 1;

          const esc =
            typeof CSS !== "undefined" && typeof CSS.escape === "function"
              ? CSS.escape(variantId)
              : variantId.replace(/["\\]/g, "\\$&");
          const card = this.querySelector(`[data-cbb-product-card][data-variant-id="${esc}"]`);
          if (!(card instanceof HTMLElement)) continue;

          const priceRaw = card.getAttribute("data-price-cents");
          const stepRaw = card.getAttribute("data-step-index");
          if (priceRaw == null || stepRaw == null) continue;

          const priceCents = parseInt(priceRaw, 10);
          const stepIndex = parseInt(stepRaw, 10);
          if (Number.isNaN(priceCents) || Number.isNaN(stepIndex)) continue;

          const imgEl = card.querySelector(".custom-bundle-builder__card-img");
          /** @type {string | undefined} */
          let imageUrl;
          /** @type {string | undefined} */
          let imageAlt;
          if (imgEl instanceof HTMLImageElement) {
            const u = (imgEl.currentSrc || imgEl.src || "").trim();
            if (u) imageUrl = u;
            const alt = (imgEl.alt || "").trim();
            if (alt) imageAlt = alt;
          }

          this.#selection.set(variantId, {
            priceCents,
            stepIndex,
            quantity: qty,
            ...(imageUrl ? { imageUrl } : {}),
            ...(imageAlt ? { imageAlt } : {}),
          });
          restoredAny = true;
        }

        const total = this.#totalSteps();
        if (restoredAny && total > 0) {
          if (storageSchema === 2) {
            const fRaw = /** @type {{ furthestVisitedStep?: unknown }} */ (parsed).furthestVisitedStep;
            let fParsed = typeof fRaw === "number" ? fRaw : parseInt(String(fRaw ?? "").trim(), 10);
            if (Number.isFinite(fParsed) && fParsed >= 1) {
              this.#furthestVisitedStep = Math.min(total, Math.max(1, Math.floor(fParsed)));
            } else {
              this.#syncFurthestVisitedFromHighestSelectedStep();
            }
          } else {
            /** Legacy payloads: derive implied progress only from SKU step indices */
            this.#syncFurthestVisitedFromHighestSelectedStep();
          }
        }

        this.#restoredSelectionNeedsBiscuitsSync = Boolean(this.#biscuitsBridgeActive() && restoredAny);
      }

      /**
       * Parse `?bundle=` using Biscuits-style encoding: comma-separated `variantId:quantity` (quantity defaults to 1).
       * @returns {Array<{ variantId: string; quantity: number }>}
       */
      #parseBundleQueryParamEntries() {
        if (typeof window === "undefined") return [];

        let raw = "";
        try {
          raw = new URLSearchParams(window.location.search).get("bundle") || "";
        } catch (e) {
          return [];
        }

        raw = raw.trim();
        if (!raw) return [];

        /** @type {Map<string, number>} */
        const byId = new Map();

        const segments = raw.split(",");
        for (let seg of segments) {
          seg = seg.trim().replace(/%3A/gi, ":");
          if (!seg) continue;

          const colon = seg.indexOf(":");
          let idPart;
          let qtyPart;
          if (colon === -1) {
            idPart = seg;
            qtyPart = "1";
          } else {
            idPart = seg.slice(0, colon).trim();
            qtyPart = seg.slice(colon + 1).trim();
          }
          if (!idPart) continue;

          const qtyNum = Number(qtyPart);
          const quantity = Number.isFinite(qtyNum) && qtyNum > 0 ? Math.floor(qtyNum) : 1;
          const prev = byId.get(idPart) || 0;
          byId.set(idPart, prev + quantity);
        }

        return [...byId.entries()].map(([variantId, quantity]) => ({ variantId, quantity }));
      }

      /**
       * When the URL includes `bundle=`, pre-select matching cards (overrides session restore). No-op if nothing matches.
       */
      #applyBundleQueryParamPrefill() {
        const parsed = this.#parseBundleQueryParamEntries();
        if (parsed.length === 0) return;

        /** @type {Map<string, { priceCents: number; stepIndex: number; quantity: number; imageUrl?: string; imageAlt?: string }>} */
        const next = new Map();

        for (const { variantId, quantity } of parsed) {
          const key = String(variantId).trim();
          if (!key) continue;

          const esc =
            typeof CSS !== "undefined" && typeof CSS.escape === "function"
              ? CSS.escape(key)
              : key.replace(/["\\]/g, "\\$&");
          const card = this.querySelector(`[data-cbb-product-card][data-variant-id="${esc}"]`);
          if (!(card instanceof HTMLElement)) continue;

          const priceRaw = card.getAttribute("data-price-cents");
          const stepRaw = card.getAttribute("data-step-index");
          if (priceRaw == null || stepRaw == null) continue;

          const priceCents = parseInt(priceRaw, 10);
          const stepIndex = parseInt(stepRaw, 10);
          if (Number.isNaN(priceCents) || Number.isNaN(stepIndex)) continue;

          const imgEl = card.querySelector(".custom-bundle-builder__card-img");
          /** @type {string | undefined} */
          let imageUrl;
          /** @type {string | undefined} */
          let imageAlt;
          if (imgEl instanceof HTMLImageElement) {
            const u = (imgEl.currentSrc || imgEl.src || "").trim();
            if (u) imageUrl = u;
            const alt = (imgEl.alt || "").trim();
            if (alt) imageAlt = alt;
          }

          next.set(key, {
            priceCents,
            stepIndex,
            quantity,
            ...(imageUrl ? { imageUrl } : {}),
            ...(imageAlt ? { imageAlt } : {}),
          });
        }

        if (next.size === 0) return;

        this.#selection.clear();
        for (const [k, v] of next) {
          this.#selection.set(k, v);
        }

        this.#tierScrollPending = true;
        this.#restoredSelectionNeedsBiscuitsSync = Boolean(this.#biscuitsBridgeActive());

        this.#syncFurthestVisitedFromHighestSelectedStep();
      }

      /**
       * After Biscuits mounts step products, mirror restored Map into app controls (initial click + quantity).
       */
      #syncRestoredSelectionToBiscuitsWhenReady() {
        if (!this.#biscuitsBridgeActive() || !this.#restoredSelectionNeedsBiscuitsSync) return;
        const root = this.#getSectionRoot();
        if (!root || !this.#sectionHasBiscuitsStepProducts(root)) return;

        this.#restoredSelectionNeedsBiscuitsSync = false;

        for (const [variantId, entry] of this.#selection) {
          const esc =
            typeof CSS !== "undefined" && typeof CSS.escape === "function"
              ? CSS.escape(variantId)
              : variantId.replace(/["\\]/g, "\\$&");
          const card = this.querySelector(`[data-cbb-product-card][data-variant-id="${esc}"]`);
          if (!(card instanceof HTMLElement)) continue;

          this.#bridgeSyncBiscuitsProduct(card);

          const q = Number.isFinite(entry.quantity) && entry.quantity > 0 ? entry.quantity : 1;
          for (let i = 1; i < q; i += 1) {
            if (!this.#bridgeClickBiscuitsQuantity(card, "plus")) break;
          }
        }

        this.#tierScrollPending = true;
        this.#refreshUI();
      }

      #nextStepTitleText() {
        const nextIdx = this.#currentStep + 1;
        const stepperEl = this.querySelector(`[data-cbb-stepper-index="${nextIdx}"]`);
        if (!(stepperEl instanceof Element)) return "";
        const titleEl = stepperEl.querySelector(".custom-bundle-builder__stepper-title");
        return titleEl ? (titleEl.textContent || "").replace(/\s+/g, " ").trim() : "";
      }

      #scheduleInitSwiper(stepIndex) {
        // Double rAF: wait until layout.width is settled (grid/container + fonts) before Swiper measures.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.#ensureSwiper(stepIndex);
          });
        });
      }

      #syncCbbSwiperNav(sw) {
        if (!sw || !sw.el) return;
        const panel = sw.el.closest("[data-cbb-step-panel]");
        const navWrap = panel?.querySelector("[data-cbb-swiper-nav-wrap]");
        if (!(navWrap instanceof HTMLElement)) return;

        const locked = Boolean(sw.isLocked);
        navWrap.hidden = locked;
        navWrap.setAttribute("aria-hidden", locked ? "true" : "false");
      }

      /** Next/Previous advance by slidesPerView columns (Swiper slidesPerGroup). */
      #swiperNavGroupByView() {
        return String(this.dataset.swiperNavGroupByView || "") === "true";
      }

      #ensureSwiper(stepIndex) {
        if (this.#swipers.has(stepIndex)) {
          const inst = this.#swipers.get(stepIndex);
          this.#reapplyTopicFilterForStep(stepIndex);
          if (inst && typeof inst.update === "function") inst.update();
          if (inst) this.#syncCbbSwiperNav(inst);
          return;
        }

        const root = this.querySelector(`[data-cbb-swiper-root="${stepIndex}"]`);
        if (!root || typeof Swiper === "undefined") {
          this.#reapplyTopicFilterForStep(stepIndex);
          return;
        }

        const panel = this.querySelector(`[data-cbb-step-panel="${stepIndex}"]`);
        if (!panel || panel.hidden) return;

        this.#dockSwiperPaginationInPanel(panel);

        const prevEl = panel.querySelector("[data-cbb-swiper-prev]");
        const nextEl = panel.querySelector("[data-cbb-swiper-next]");
        const paginationEl = panel.querySelector("[data-cbb-swiper-pagination]");
        const navOk = prevEl instanceof HTMLElement && nextEl instanceof HTMLElement;
        const paginationOk = paginationEl instanceof HTMLElement;
        const stackedLayout = this.#isNarrowBundleLayout();

        const groupByView = this.#swiperNavGroupByView();

        try {
          const swiper = new Swiper(root, {
            slidesPerView: 2,
            spaceBetween: 5,
            ...(groupByView ? { slidesPerGroup: 2 } : {}),
            grid: {
              rows: 2,
              fill: "row",
            },
            watchOverflow: true,
            breakpointsBase: "window",
            breakpoints: {
              576: {
                slidesPerView: 3,
                spaceBetween: 10,
                ...(groupByView ? { slidesPerGroup: 3 } : {}),
              },
              1200: {
                slidesPerView: 4,
                spaceBetween: 16,
                ...(groupByView ? { slidesPerGroup: 4 } : {}),
              },
            },
            navigation: navOk
              ? {
                prevEl,
                nextEl,
              }
              : undefined,
            pagination: paginationOk
              ? stackedLayout
                ? {
                  el: paginationEl,
                  clickable: true,
                  type: "bullets",
                }
                : {
                  el: paginationEl,
                  type: "progressbar",
                }
              : undefined,
            on: {
              init: (sw) => {
                requestAnimationFrame(() => {
                  if (typeof sw.update === "function") sw.update();
                  this.#syncCbbSwiperNav(sw);
                });
              },
              resize: (sw) => this.#syncCbbSwiperNav(sw),
              breakpoint: (sw) => this.#syncCbbSwiperNav(sw),
              update: (sw) => this.#syncCbbSwiperNav(sw),
              lock: (sw) => this.#syncCbbSwiperNav(sw),
              unlock: (sw) => this.#syncCbbSwiperNav(sw),
            },
          });
          this.#swipers.set(stepIndex, swiper);
          this.#reapplyTopicFilterForStep(stepIndex);
          if (typeof swiper.update === "function") swiper.update();
          this.#syncCbbSwiperNav(swiper);
        } catch (e) {
          console.warn("CustomBundleBuilder: Swiper init failed", e);
          this.#swipers.set(stepIndex, null);
          this.#reapplyTopicFilterForStep(stepIndex);
        }
      }

      #destroySwipers() {
        for (const [, inst] of this.#swipers) {
          if (inst && typeof inst.destroy === "function") {
            try {
              inst.destroy(true, true);
            } catch (e) {
              /* ignore */
            }
          }
        }
        this.#swipers.clear();
      }

      /** @param {HTMLElement} panel */
      #collectTopicFilterGroupRoots(panel) {
        const named = [...panel.querySelectorAll("[data-cbb-topic-filter-group]")];
        if (named.length) {
          return named.map((root) => ({
            key: root.getAttribute("data-cbb-topic-filter-group") || "primary",
            root,
          }));
        }
        const legacyHost = panel.querySelector("[data-cbb-topic-filters]");
        if (legacyHost instanceof HTMLElement) return [{ key: "primary", root: legacyHost }];
        if (panel.querySelector("[data-cbb-topic-filter]")) return [{ key: "primary", root: panel }];
        return [];
      }

      /** @param {number} stepIndex */
      #getStepTopicFilterGroupMap(stepIndex) {
        let m = this.#topicFilterGroupsByStep.get(stepIndex);
        if (!m) {
          m = new Map();
          this.#topicFilterGroupsByStep.set(stepIndex, m);
        }
        return m;
      }

      /**
       * @param {HTMLElement} panel
       * @param {Map<string, string>} groupMap
       * @returns {string[]}
       */
      #resolveRequiredTopicTagNorms(panel, groupMap) {
        const groups = this.#collectTopicFilterGroupRoots(panel);
        const required = [];
        for (const { key, root } of groups) {
          const idFromMap = groupMap.get(key);
          let btn =
            typeof idFromMap === "string" && idFromMap !== ""
              ? root.querySelector(`[data-cbb-topic-filter-id="${idFromMap}"]`)
              : null;
          if (!(btn instanceof HTMLElement)) {
            btn =
              root.querySelector(".custom-bundle-builder__topic-filter--active") ||
              root.querySelector('[data-cbb-topic-filter][aria-pressed="true"]') ||
              root.querySelector("[data-cbb-topic-filter]");
          }
          if (!(btn instanceof HTMLElement)) continue;
          const mode = btn.getAttribute("data-cbb-topic-filter-mode") || "all";
          const rawTag = btn.getAttribute("data-cbb-topic-filter-tag") || "";
          const tagNorm = rawTag.trim().toLowerCase();
          if (mode === "tag" && tagNorm) required.push(tagNorm);
        }
        return required;
      }

      /** @param {number} stepIndex */
      #applyCombinedTopicFiltersForStep(stepIndex) {
        const panel = this.querySelector(`[data-cbb-step-panel="${stepIndex}"]`);
        if (!(panel instanceof HTMLElement)) return;

        const groups = this.#collectTopicFilterGroupRoots(panel);
        if (!groups.length) return;

        const groupMap = this.#getStepTopicFilterGroupMap(stepIndex);
        const requiredTags = this.#resolveRequiredTopicTagNorms(panel, groupMap);

        const root = panel.querySelector("[data-cbb-swiper-root]");
        if (!root) return;
        const wrap = root.querySelector(".swiper-wrapper");
        if (!(wrap instanceof HTMLElement)) return;

        wrap.querySelectorAll(":scope > *").forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          const card = node.querySelector("[data-cbb-product-card]");
          if (!(card instanceof HTMLElement)) {
            node.classList.add("swiper-slide");
            node.classList.remove("custom-bundle-builder__swiper-slide--hidden");
            return;
          }

          let tags = [];
          const rawTags = card.getAttribute("data-cbb-product-tags") || "[]";
          try {
            const parsed = JSON.parse(rawTags);
            if (Array.isArray(parsed)) tags = parsed;
          } catch (e) {
            tags = [];
          }
          const normalized = new Set(tags.map((x) => String(x).trim().toLowerCase()).filter(Boolean));
          const show = requiredTags.length === 0 || requiredTags.every((tag) => normalized.has(tag));

          if (show) {
            node.classList.add("swiper-slide");
            node.classList.remove("custom-bundle-builder__swiper-slide--hidden");
          } else {
            node.classList.remove("swiper-slide");
            node.classList.add("custom-bundle-builder__swiper-slide--hidden");
          }
        });

        const swiper = this.#swipers.get(stepIndex);
        if (swiper) {
          const visibleCount = wrap.querySelectorAll(".swiper-slide").length;
          requestAnimationFrame(() => {
            if (typeof swiper.updateSlides === "function") swiper.updateSlides();
            if (typeof swiper.updateSize === "function") swiper.updateSize();
            if (typeof swiper.updateProgress === "function") swiper.updateProgress();
            if (typeof swiper.update === "function") swiper.update();
            if (typeof swiper.slideTo === "function" && visibleCount > 0) {
              swiper.slideTo(0, 0);
            }
            if (swiper.pagination && typeof swiper.pagination.update === "function") {
              swiper.pagination.update();
            }
            if (swiper.navigation && typeof swiper.navigation.update === "function") {
              swiper.navigation.update();
            }
            this.#syncCbbSwiperNav(swiper);
          });
        }
      }

      /** @param {number} stepIndex */
      #reapplyTopicFilterForStep(stepIndex) {
        const panel = this.querySelector(`[data-cbb-step-panel="${stepIndex}"]`);
        if (!(panel instanceof HTMLElement)) return;

        const groups = this.#collectTopicFilterGroupRoots(panel);
        if (!groups.length) return;

        const groupMap = this.#getStepTopicFilterGroupMap(stepIndex);

        for (const { key, root } of groups) {
          const idFromMap = groupMap.get(key);
          let btn =
            typeof idFromMap === "string" && idFromMap !== ""
              ? root.querySelector(`[data-cbb-topic-filter-id="${idFromMap}"]`)
              : null;
          if (!(btn instanceof HTMLElement)) {
            btn =
              root.querySelector('[data-cbb-topic-filter][aria-pressed="true"]') ||
              root.querySelector("[data-cbb-topic-filter]");
          }
          if (!(btn instanceof HTMLElement)) continue;

          const filterId = btn.getAttribute("data-cbb-topic-filter-id") || "";
          groupMap.set(key, filterId);

          root.querySelectorAll("[data-cbb-topic-filter]").forEach((b) => {
            if (!(b instanceof HTMLElement)) return;
            const on = b === btn;
            b.classList.toggle("custom-bundle-builder__topic-filter--active", on);
            b.setAttribute("aria-pressed", on ? "true" : "false");
          });
        }

        this.#applyCombinedTopicFiltersForStep(stepIndex);
      }

      /** @param {HTMLElement} btn */
      #topicFilterFromButton(btn) {
        const panel = btn.closest("[data-cbb-step-panel]");
        if (!(panel instanceof HTMLElement)) return;

        const stepRaw = panel.getAttribute("data-cbb-step-panel");
        const stepIndex = parseInt(stepRaw || "0", 10);
        if (Number.isNaN(stepIndex) || stepIndex < 1) return;

        let groupRoot = btn.closest("[data-cbb-topic-filter-group]");
        if (!(groupRoot instanceof HTMLElement)) {
          const legacy = btn.closest("[data-cbb-topic-filters]");
          groupRoot = legacy instanceof HTMLElement ? legacy : btn.closest(".custom-bundle-builder__topic-filters-row");
        }
        if (!(groupRoot instanceof HTMLElement)) return;

        const groupKey =
          groupRoot instanceof HTMLElement && groupRoot.hasAttribute("data-cbb-topic-filter-group")
            ? groupRoot.getAttribute("data-cbb-topic-filter-group") || "primary"
            : "primary";

        const filterId = btn.getAttribute("data-cbb-topic-filter-id") || "";
        this.#getStepTopicFilterGroupMap(stepIndex).set(groupKey, filterId);

        groupRoot.querySelectorAll("[data-cbb-topic-filter]").forEach((b) => {
          if (!(b instanceof HTMLElement)) return;
          const on = b === btn;
          b.classList.toggle("custom-bundle-builder__topic-filter--active", on);
          b.setAttribute("aria-pressed", on ? "true" : "false");
        });

        this.#applyCombinedTopicFiltersForStep(stepIndex);
      }

      /** @param {MouseEvent} e */
      #onClick(e) {
        const t = e.target;
        if (!(t instanceof Element)) return;

        const jumper = t.closest("[data-cbb-stepper-jump]");
        if (jumper instanceof HTMLElement) {
          if (jumper.getAttribute("aria-disabled") !== "false") return;
          const target = parseInt(jumper.getAttribute("data-cbb-stepper-jump") || "0", 10);

          if (this.#stepperJumpAllowed(target)) {
            e.preventDefault();
            this.#setBuilderError(false);
            this.#goToStep(target);
          }

          return;
        }

        const mobileSummaryToggle = t.closest("[data-cbb-mobile-summary-toggle]");
        if (mobileSummaryToggle instanceof HTMLButtonElement) {
          e.preventDefault();
          this.#toggleMobileSummaryDrawer(mobileSummaryToggle);
          return;
        }

        const topicFilterBtn = t.closest("[data-cbb-topic-filter]");
        if (topicFilterBtn instanceof HTMLElement) {
          e.preventDefault();
          this.#topicFilterFromButton(topicFilterBtn);
          return;
        }

        const tierCellBtn = t.closest("[data-cbb-tier-cell]");
        if (tierCellBtn instanceof HTMLElement && tierCellBtn.getAttribute("data-cbb-tier-filled") === "true") {
          e.preventDefault();
          const slotRaw = tierCellBtn.getAttribute("data-cbb-tier-slot-index") || "0";
          const slotIndex = parseInt(slotRaw, 10);
          if (Number.isFinite(slotIndex) && slotIndex > 0) {
            this.#onTierFilledCellClick(slotIndex);
          }
          return;
        }

        if (t.closest("[data-cbb-product-link]")) return;

        const qtyPlusEl = t.closest("[data-cbb-qty-plus]");
        const qtyMinusEl = t.closest("[data-cbb-qty-minus]");
        if (qtyPlusEl || qtyMinusEl) {
          e.preventDefault();
          e.stopPropagation();
          const card = (qtyPlusEl || qtyMinusEl)?.closest("[data-cbb-product-card]");
          if (card instanceof HTMLElement) {
            this.#adjustCardQuantity(card, qtyPlusEl ? 1 : -1);
          }
          return;
        }

        const addBtn = t.closest("[data-cbb-add]");
        if (addBtn) {
          e.preventDefault();
          e.stopPropagation();
          this.#toggleCard(addBtn);
          return;
        }

        if (t.closest("[data-cbb-product-card]") && t.closest("[data-cbb-qty-wrap]")) return;

        const card = t.closest("[data-cbb-product-card]");
        if (card instanceof HTMLElement) {
          const nestedLink = card.querySelector("[data-cbb-product-link]");
          if (nestedLink && nestedLink.contains(t)) return;

          const btnInCard = card.querySelector("[data-cbb-add]");
          if (btnInCard instanceof HTMLElement) {
            e.preventDefault();
            this.#toggleCard(btnInCard);
          }
          return;
        }

        const backCta = t.closest("[data-cbb-back-cta]");
        if (backCta instanceof HTMLButtonElement) {
          if (backCta.disabled) return;
          e.preventDefault();
          this.#setBuilderError(false);
          this.#goToStep(this.#currentStep - 1);
          return;
        }

        const cta = t.closest("[data-cbb-primary-cta]");
        if (cta) {
          e.preventDefault();
          this.#onPrimaryCta();
        }
      }

      /** @param {KeyboardEvent} e */
      #onKeyDown(e) {
        if (e.key !== "Enter" && e.key !== " ") return;
        const t = e.target;
        if (!(t instanceof Element)) return;

        const jumper = t.closest("[data-cbb-stepper-jump]");
        if (jumper instanceof HTMLElement && jumper.getAttribute("aria-disabled") === "false") {
          const target = parseInt(jumper.getAttribute("data-cbb-stepper-jump") || "0", 10);

          if (this.#stepperJumpAllowed(target)) {
            e.preventDefault();
            this.#setBuilderError(false);
            this.#goToStep(target);
          }

          return;
        }

        const qtyPlusEl = t.closest("[data-cbb-qty-plus]");
        const qtyMinusEl = t.closest("[data-cbb-qty-minus]");
        if (qtyPlusEl || qtyMinusEl) {
          e.preventDefault();
          const card = (qtyPlusEl || qtyMinusEl)?.closest("[data-cbb-product-card]");
          if (card instanceof HTMLElement) {
            this.#adjustCardQuantity(card, qtyPlusEl ? 1 : -1);
          }
          return;
        }

        const addBtn = t.closest("[data-cbb-add]");
        if (addBtn) {
          e.preventDefault();
          this.#toggleCard(addBtn);
          return;
        }

        const tierKb = t.closest("[data-cbb-tier-cell]");
        if (tierKb instanceof HTMLElement && tierKb.getAttribute("data-cbb-tier-filled") === "true") {
          e.preventDefault();
          const slotRaw = tierKb.getAttribute("data-cbb-tier-slot-index") || "0";
          const slotIndex = parseInt(slotRaw, 10);
          if (Number.isFinite(slotIndex) && slotIndex > 0) {
            this.#onTierFilledCellClick(slotIndex);
          }
          return;
        }
      }

      /** @param {Element} addBtn */
      #toggleCard(addBtn) {
        const card = addBtn.closest("[data-cbb-product-card]");
        if (!card) return;

        const variantId = card.getAttribute("data-variant-id");
        const priceRaw = card.getAttribute("data-price-cents");
        const stepRaw = card.getAttribute("data-step-index");

        if (!variantId || priceRaw == null || stepRaw == null) return;

        const priceCents = parseInt(priceRaw, 10);
        const stepIndex = parseInt(stepRaw, 10);

        if (Number.isNaN(priceCents) || Number.isNaN(stepIndex)) return;

        const key = String(variantId);

        if (this.#selection.has(key)) {
          this.#selection.delete(key);
          card.classList.remove("custom-bundle-builder__card--selected");
          addBtn.setAttribute("aria-pressed", "false");
          if (this.#selection.size === 0) {
            this.#finalizeEmptyBundleNavigation();
            this.#bridgeSyncBiscuitsProduct(card);
            return;
          }
        } else {
          const imgEl = card.querySelector(".custom-bundle-builder__card-img");
          /** @type {string | undefined} */
          let imageUrl;
          /** @type {string | undefined} */
          let imageAlt;
          if (imgEl instanceof HTMLImageElement) {
            const u = (imgEl.currentSrc || imgEl.src || "").trim();
            if (u) imageUrl = u;
            const alt = (imgEl.alt || "").trim();
            if (alt) imageAlt = alt;
          }
          this.#selection.set(key, {
            priceCents,
            stepIndex,
            quantity: 1,
            ...(imageUrl ? { imageUrl } : {}),
            ...(imageAlt ? { imageAlt } : {}),
          });
          card.classList.add("custom-bundle-builder__card--selected");
          addBtn.setAttribute("aria-pressed", "true");
          this.#tierScrollPending = true;
        }

        this.#refreshUI();
        this.#bridgeSyncBiscuitsProduct(card);
      }

      /**
       * Clicks Biscuits quantity control inside the matched product host when bridge is active.
       * @param {HTMLElement} card
       * @param {"minus"|"plus"} which
       * @returns {boolean} true when a clickable app control fired
       */
      #bridgeClickBiscuitsQuantity(card, which) {
        if (!this.#biscuitsBridgeActive()) return true;
        const variantId = card.getAttribute("data-variant-id");
        if (!variantId) return false;
        const host = this.#findBiscuitsProductHostForVariant(variantId);
        if (!(host instanceof HTMLElement)) return false;
        const btn =
          which === "minus"
            ? host.querySelector(".biscuits-bundle-quantity__button--minus")
            : host.querySelector(".biscuits-bundle-quantity__button--plus");
        if (!(btn instanceof HTMLElement)) return false;
        if (btn.classList.contains("biscuits--disabled")) return false;
        btn.click();
        return true;
      }

      /** @param {HTMLElement} card @param {1 | -1} delta */
      #adjustCardQuantity(card, delta) {
        const variantId = card.getAttribute("data-variant-id");
        if (!variantId) return;

        const key = String(variantId);
        const entry = this.#selection.get(key);
        if (!entry) return;

        const qty = Number.isFinite(entry.quantity) && entry.quantity > 0 ? entry.quantity : 1;

        if (delta > 0) {
          const which = "plus";
          if (!this.#bridgeClickBiscuitsQuantity(card, which)) return;
          entry.quantity = qty + 1;
          const imgEl = card.querySelector(".custom-bundle-builder__card-img");
          if (imgEl instanceof HTMLImageElement) {
            const u = (imgEl.currentSrc || imgEl.src || "").trim();
            if (u) entry.imageUrl = u;
            const alt = (imgEl.alt || "").trim();
            if (alt) entry.imageAlt = alt;
          }
          this.#tierScrollPending = true;
          this.#refreshUI();
          return;
        }

        const which = "minus";

        if (this.#biscuitsBridgeActive()) {
          const bridged = this.#bridgeClickBiscuitsQuantity(card, which);
          if (!bridged) return;
          if (qty <= 1) {
            this.#selection.delete(key);
            card.classList.remove("custom-bundle-builder__card--selected");
            const addBtn = card.querySelector("[data-cbb-add]");
            if (addBtn instanceof HTMLElement) {
              addBtn.setAttribute("aria-pressed", "false");
            }
          } else {
            entry.quantity = qty - 1;
          }
          if (this.#selection.size === 0) {
            this.#finalizeEmptyBundleNavigation();
            return;
          }
          this.#refreshUI();
          return;
        }

        if (qty <= 1) {
          this.#selection.delete(key);
          card.classList.remove("custom-bundle-builder__card--selected");
          const addBtn = card.querySelector("[data-cbb-add]");
          if (addBtn instanceof HTMLElement) {
            addBtn.setAttribute("aria-pressed", "false");
          }
        } else {
          entry.quantity = qty - 1;
        }
        if (this.#selection.size === 0) {
          this.#finalizeEmptyBundleNavigation();
        } else {
          this.#refreshUI();
        }
      }

      /** @param {boolean} visible @param {string} [message] */
      #setBuilderError(visible, message) {
        const el = this.querySelector("[data-cbb-builder-error]");
        if (!(el instanceof HTMLElement)) return;

        if (!visible) {
          el.textContent = "";
          el.hidden = true;
          return;
        }

        el.textContent = message !== undefined && String(message).trim() !== "" ? String(message) : "";
        el.hidden = String(el.textContent).trim() === "";
      }

      /** @returns {boolean} User has opened the final step at least once (Continue or stepper). */
      #hasVisitedLastBuilderStep() {
        return this.#furthestVisitedStep >= this.#totalSteps();
      }

      /** @returns {boolean} Finalize from any step only after visiting the last step and all mins are met. */
      #canFinalizeFromPrimaryCta() {
        return this.#hasVisitedLastBuilderStep() && this.#allStepsSatisfyMin();
      }

      #onPrimaryCta() {
        const total = this.#totalSteps();

        if (this.#canFinalizeFromPrimaryCta()) {
          this.#finalizeBundleFromPrimaryCta();
          return;
        }

        if (this.#currentStep < total) {
          if (!this.#stepSatisfiesMin(this.#currentStep)) return;
          this.#setBuilderError(false);
          this.#goToStep(this.#currentStep + 1);
          return;
        }
      }

      /** Add to cart / redirect when bundle requirements are met (any step). */
      #finalizeBundleFromPrimaryCta() {
        if (this.#biscuitsBridgeActive()) {
          const atc = this.#findCbbThemeAddToCartButton();
          if (atc instanceof HTMLElement) {
            if (atc.classList.contains("biscuits--disabled")) {
              const errText = this.#findBiscuitsFormErrorMessage();
              this.#setBuilderError(
                true,
                errText || "Unable to add this bundle yet. Complete any missing selections required by the bundle form above."
              );
              return;
            }
            this.#setBuilderError(false);
            this.#delegateToBiscuitsAddToCart(atc);
            return;
          }
          this.#setBuilderError(true, "Bundle checkout is not ready. Add the “Buy buttons” block, choose a Biscuits bundle product, then try again.");
          return;
        }

        const url = this.#buildBundleUrl();
        if (!url) return;

        this.#setBuilderError(false);

        window.location.href = url;
      }

      /** @param {number} step */
      #stepperJumpAllowed(step) {
        if (step === this.#currentStep) return false;
        const total = this.#totalSteps();
        if (step < 1 || step > total) return false;

        if (step <= this.#furthestVisitedStep && step !== this.#currentStep) {
          return true;
        }

        if (step > this.#currentStep) {
          for (let s = this.#currentStep; s < step; s += 1) {
            if (!this.#stepSatisfiesMin(s)) return false;
          }
          return true;
        }

        return false;
      }

      /** @param {number} step */
      #goToStep(step) {
        const total = this.#totalSteps();
        if (step < 1 || step > total) return;

        const prevStep = this.#currentStep;

        if (step > this.#furthestVisitedStep) {
          this.#furthestVisitedStep = step;
        }
        this.#currentStep = step;

        for (let s = 1; s <= total; s += 1) {
          const panel = this.querySelector(`[data-cbb-step-panel="${s}"]`);
          if (!panel) continue;

          if (s === step) {
            panel.hidden = false;
            panel.classList.add("custom-bundle-builder__step-panel--active");
          } else {
            panel.hidden = true;
            panel.classList.remove("custom-bundle-builder__step-panel--active");
          }
        }

        this.#scheduleInitSwiper(step);
        this.#setBuilderError(false);
        this.#refreshUI();

        if (step !== prevStep) {
          this.#scrollBuilderInnerIntoView();
        }
      }

      /** @param {number} step */
      #countForStep(step) {
        let n = 0;
        for (const entry of this.#selection.values()) {
          if (entry.stepIndex !== step) continue;
          const q = Number.isFinite(entry.quantity) && entry.quantity > 0 ? entry.quantity : 1;
          n += q;
        }
        return n;
      }

      #totalSelectedItemCount() {
        let n = 0;
        for (const entry of this.#selection.values()) {
          const q = Number.isFinite(entry.quantity) && entry.quantity > 0 ? entry.quantity : 1;
          n += q;
        }
        return n;
      }

      /** @param {number} step */
      #stepSatisfiesMin(step) {
        const min = this.#config.stepMinPicks[step - 1] ?? 0;
        return this.#countForStep(step) >= min;
      }

      #allStepsSatisfyMin() {
        for (let s = 1; s <= this.#totalSteps(); s += 1) {
          if (!this.#stepSatisfiesMin(s)) return false;
        }
        return true;
      }

      /** First step (1-based) whose minimum pick count is not met, or null when all pass. */
      #findFirstStepWithUnmetMinimum() {
        const total = this.#totalSteps();
        for (let s = 1; s <= total; s += 1) {
          if (!this.#stepSatisfiesMin(s)) return s;
        }
        return null;
      }

      /**
       * Sets `#furthestVisitedStep` from `max(selection.stepIndex)` (clamped). Matches v2 restores / optional steps.
       */
      #syncFurthestVisitedFromHighestSelectedStep() {
        const total = this.#totalSteps();
        if (total <= 0 || this.#selection.size === 0) return;

        let maxSi = 0;
        for (const row of this.#selection.values()) {
          const si = row.stepIndex;
          if (!Number.isFinite(si)) continue;
          if (si > maxSi) maxSi = si;
        }
        if (maxSi <= 0) return;
        this.#furthestVisitedStep = Math.min(total, Math.max(1, Math.floor(maxSi)));
      }

      /** When removals break mandated progress or current panel sits past them, retract furthest and jump back (no extra UI vs v2 remove-all behavior). */
      #enforceMandatoryStepProgressConsistency() {
        if (this.#selection.size === 0) return false;
        const blocked = this.#findFirstStepWithUnmetMinimum();
        if (blocked == null) return false;
        this.#furthestVisitedStep = Math.min(this.#furthestVisitedStep, blocked);
        if (this.#currentStep > blocked) {
          this.#goToStep(blocked);
          return true;
        }
        return false;
      }

      /**
       * Parity with v2: empty selection resets scope (`Create bundle` / visited-last flags until revisiting late steps).
       */
      #finalizeEmptyBundleNavigation() {
        this.#tierScrollPending = true;
        this.#setBuilderError(false);
        this.#furthestVisitedStep = 1;
        const prevStep = this.#currentStep;
        this.#goToStep(1);
        if (prevStep === 1) {
          this.#scrollBuilderInnerIntoView();
        }
      }

      /** @param {number} count */
      #bestDiscountPercent(count) {
        let best = 0;
        const tiers = this.#config.discountTiers || [];
        for (const tier of tiers) {
          const th = tier.threshold || 0;
          const pct = tier.percent || 0;
          if (count >= th && pct > best) best = pct;
        }
        return best;
      }

      /** @returns {{ need: number; pct: number } | null} */
      #nextTierProgressMeta(selCount) {
        const tiers = this.#config.discountTiers || [];
        if (!Array.isArray(tiers) || tiers.length === 0) return null;

        const next = tiers.find((tier) => selCount < (tier.threshold ?? 0));
        if (!next) return null;

        const th = Number(next.threshold ?? 0);
        const pct = Number(next.percent ?? 0);
        if (!Number.isFinite(th) || th <= 0) return null;

        const need = Math.max(0, th - selCount);
        if (need <= 0) return null;

        return { need, pct };
      }

      #maxDiscountThreshold() {
        let m = 0;
        const tiers = this.#config.discountTiers || [];
        for (const tier of tiers) {
          const th = Number(tier.threshold ?? 0);
          if (Number.isFinite(th) && th > m) m = th;
        }
        return m;
      }

      /** @param {number} index One-based bundle position; same as Discount tier threshold index. */
      #milestonePercentAtIndex(index) {
        /** @type {number | null} */
        let best = null;
        const tiers = this.#config.discountTiers || [];
        for (const tier of tiers) {
          if (Number(tier.threshold ?? 0) !== index) continue;
          const p = Number(tier.percent ?? 0);
          if (!Number.isFinite(p)) continue;
          if (best == null || p > best) best = p;
        }
        return best;
      }

      /** @returns {string[]} Variant ids in Map order, each repeated by quantity. */
      #flattenSelectionUnits() {
        const out = [];
        for (const [key, entry] of this.#selection) {
          const q = Number.isFinite(entry.quantity) && entry.quantity > 0 ? entry.quantity : 1;
          for (let i = 0; i < q; i += 1) {
            out.push(key);
          }
        }
        return out;
      }

      /** @param {HTMLElement} el */
      #fillTierProgressTitle(el) {
        el.textContent = "";
        const meta = this.#nextTierProgressMeta(this.#totalSelectedItemCount());
        if (!meta) return;

        const tplRaw = this.#config.tierProgressTemplate;
        const tpl = typeof tplRaw === "string" ? tplRaw.trim() : "";
        let template = tpl !== "" ? tpl : "Pick __NEED__ more to get __PCT__% off";
        if (meta.need === 1) {
          const singularRaw = this.#config.tierProgressTemplateSingular;
          const singular = typeof singularRaw === "string" ? singularRaw.trim() : "";
          if (singular !== "") template = singular;
        }
        const withNeed = template.replace(/__NEED__/g, String(meta.need));
        const parts = withNeed.split("__PCT__");
        const before = parts[0] ?? "";
        const after = parts.slice(1).join("__PCT__");

        if (before) el.append(document.createTextNode(before));

        const badge = document.createElement("span");
        badge.className = "custom-bundle-builder__tier-progress-badge";
        badge.textContent = `${meta.pct}%`;
        el.append(badge);

        if (after) el.append(document.createTextNode(after));
      }

      /**
       * @param {string | undefined | null} sourceAlt
       * @param {string} fallback
       * @returns {string}
       */
      #resolveImageAlt(sourceAlt, fallback) {
        const alt = (sourceAlt || "").trim();
        return alt || fallback;
      }

      /**
       * @param {HTMLElement} track
       * @param {number} N
       * @param {number} selCount
       * @param {string[]} flat
       */
      #renderTierTrack(track, N, selCount, flat) {
        track.textContent = "";

        for (let i = 1; i <= N; i += 1) {
          const ms = this.#milestonePercentAtIndex(i);
          const filled = i <= selCount;
          const variantKey = filled ? flat[i - 1] : null;

          const li = document.createElement("li");
          li.className = "custom-bundle-builder__tier-item";
          li.setAttribute("data-cbb-tier-slot-index", String(i));

          if (filled && variantKey) {
            const btn = document.createElement("button");
            btn.type = "button";
            btn.className = "custom-bundle-builder__tier-cell custom-bundle-builder__tier-cell--filled";
            if (ms != null) btn.classList.add("custom-bundle-builder__tier-cell--milestone");
            btn.setAttribute("data-cbb-tier-cell", "");
            btn.setAttribute("data-cbb-tier-filled", "true");
            btn.setAttribute("data-cbb-tier-slot-index", String(i));
            btn.setAttribute("aria-label", `Remove item ${i} from bundle`);

            const entry = this.#selection.get(variantKey);
            const src = (entry && entry.imageUrl) || "";

            if (src) {
              const img = document.createElement("img");
              img.className = "custom-bundle-builder__tier-cell-img";
              img.src = src;
              img.alt = this.#resolveImageAlt(entry?.imageAlt, `Bundle item ${i}`);
              btn.append(img);
            }

            const idx = document.createElement("span");
            idx.className = "custom-bundle-builder__tier-cell-index";
            idx.textContent = String(i);
            btn.append(idx);

            if (ms != null) {
              const pctEl = document.createElement("span");
              pctEl.className = "custom-bundle-builder__tier-cell-badge-pct";
              pctEl.textContent = `-${ms}%`;
              btn.append(pctEl);
            }

            li.append(btn);
            track.append(li);
            continue;
          }

          const cell = document.createElement("div");
          cell.className = "custom-bundle-builder__tier-cell custom-bundle-builder__tier-cell--empty";
          if (ms != null) {
            cell.classList.add("custom-bundle-builder__tier-cell--milestone");
            cell.textContent = `-${ms}%`;
          } else {
            cell.textContent = String(i);
          }
          li.append(cell);
          track.append(li);
        }
      }

      #updateTierStrip() {
        const wrap = this.querySelector("[data-cbb-tier]");
        const titleEl = this.querySelector("[data-cbb-tier-progress-title]");
        const track = this.querySelector("[data-cbb-tier-track]");
        const scrollHost = this.querySelector("[data-cbb-tier-track-wrap]");

        if (!(wrap instanceof HTMLElement) || !(titleEl instanceof HTMLElement) || !(track instanceof HTMLElement)) {
          return;
        }

        const selCount = this.#totalSelectedItemCount();
        const maxThr = this.#maxDiscountThreshold();
        const showTier = maxThr > 0 || selCount > 0;

        if (!showTier) {
          wrap.hidden = true;
          titleEl.textContent = "";
          track.textContent = "";
          return;
        }

        wrap.hidden = false;
        const N = Math.max(maxThr, selCount);
        this.#fillTierProgressTitle(titleEl);

        const flat = this.#flattenSelectionUnits();
        const shouldScroll = this.#tierScrollPending;
        this.#tierScrollPending = false;

        this.#renderTierTrack(track, N, selCount, flat);

        if (shouldScroll && selCount > 0 && scrollHost instanceof HTMLElement) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              const cell = track.querySelector(`[data-cbb-tier-slot-index="${selCount}"]`);
              if (cell instanceof HTMLElement) {
                cell.scrollIntoView({ block: "nearest", inline: "end", behavior: "smooth" });
              }
            });
          });
        }
      }

      /** @param {string} key */
      #decrementSelectionVariantKey(key) {
        const entry = this.#selection.get(key);
        if (!entry) return;

        const qty = Number.isFinite(entry.quantity) && entry.quantity > 0 ? entry.quantity : 1;
        const esc =
          typeof CSS !== "undefined" && typeof CSS.escape === "function"
            ? CSS.escape(key)
            : key.replace(/["\\]/g, "\\$&");
        const card = this.querySelector(`[data-cbb-product-card][data-variant-id="${esc}"]`);

        if (qty <= 1) {
          this.#selection.delete(key);
          if (card instanceof HTMLElement) {
            card.classList.remove("custom-bundle-builder__card--selected");
            const addBtn = card.querySelector("[data-cbb-add]");
            if (addBtn instanceof HTMLElement) {
              addBtn.setAttribute("aria-pressed", "false");
            }
          }
        } else {
          entry.quantity = qty - 1;
        }
      }

      /** @param {number} slotIndex One-based tier tile index (matches bundle position). */
      #onTierFilledCellClick(slotIndex) {
        const selCount = this.#totalSelectedItemCount();
        if (slotIndex < 1 || slotIndex > selCount) return;

        const flat = this.#flattenSelectionUnits();
        const keyTarget = flat[slotIndex - 1];
        if (!keyTarget) return;

        const esc =
          typeof CSS !== "undefined" && typeof CSS.escape === "function"
            ? CSS.escape(keyTarget)
            : keyTarget.replace(/["\\]/g, "\\$&");
        const card = this.querySelector(`[data-cbb-product-card][data-variant-id="${esc}"]`);

        if (this.#biscuitsBridgeActive()) {
          if (!(card instanceof HTMLElement)) return;
          if (!this.#bridgeClickBiscuitsQuantity(card, "minus")) return;
        }

        this.#decrementSelectionVariantKey(keyTarget);
        if (this.#selection.size === 0) {
          this.#finalizeEmptyBundleNavigation();
          return;
        }
        this.#refreshUI();
      }

      #stripMoneyHtml(raw) {
        if (raw == null) return "";

        let s = String(raw).trim();
        if (s === "") return "";

        if (!s.includes("<")) return s;

        if (typeof document !== "undefined" && document.createElement) {
          const tmp = document.createElement("template");
          tmp.innerHTML = s;

          let text = tmp.content.textContent || tmp.content.innerText || "";

          text = text.replace(/\s+/g, " ").trim();

          return text !== "" ? text : s.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
        }

        return s.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
      }

      #formatMoneyCents(cents) {
        const num = Number(cents);
        /** @type {string} */

        try {
          if (typeof window.formatMoney === "function") {
            return this.#stripMoneyHtml(window.formatMoney(num));
          }
        } catch (e) {
          /* fall through */
        }

        const safe = Number.isFinite(num) ? num : 0;

        return this.#stripMoneyHtml((safe / 100).toFixed(2));
      }

      #buildSummaryCountText(selCount) {
        let t = (this.#config.summaryCountTemplate || "").replace(/__COUNT__/g, String(selCount));
        t = t.replace(/\s*\/\s*__MAX__/g, "").replace(/__MAX__/g, "").replace(/\s{2,}/g, " ").trim();
        return t;
      }

      #getStepSummaryHint() {
        const hints = this.#config.stepSummaryHints || [];
        const stepHint = (hints[this.#currentStep - 1] || "").trim();
        if (stepHint) return stepHint;
        return (this.#config.summaryHint || "").trim();
      }

      #buildBundleUrl() {
        const base = (this.dataset.bundleBaseUrl || "").trim();
        if (!base || this.#selection.size === 0) return "";

        const params = [...this.#selection.entries()]
          .map(([id, row]) => {
            const q = Number.isFinite(row.quantity) && row.quantity > 0 ? row.quantity : 1;
            return `${encodeURIComponent(id)}%3A${encodeURIComponent(String(q))}`;
          })
          .join("%2C");

        let url = base.includes("?") ? `${base}&bundle=${params}` : `${base}?bundle=${params}`;

        return url;
      }

      #refreshUI() {
        if (this.#enforceMandatoryStepProgressConsistency()) return;
        this.#updateSummary();
        this.#updateStepper();
        this.#updateCta();
        this.#updateBackCta();
        this.#syncCardsWithSelection();
        this.#updateTierStrip();
        this.#scheduleIntroActionsSlotStuckUpdate();
        this.#schedulePersistSelectionToStorage();
      }

      #syncCardsWithSelection() {
        this.querySelectorAll("[data-cbb-product-card]").forEach((card) => {
          const id = card.getAttribute("data-variant-id");
          const btn = card.querySelector("[data-cbb-add]");
          if (!id || !btn) return;
          const on = this.#selection.has(String(id));
          card.classList.toggle("custom-bundle-builder__card--selected", on);
          btn.setAttribute("aria-pressed", on ? "true" : "false");
          const wrap = card.querySelector("[data-cbb-qty-wrap]");
          const valEl = card.querySelector("[data-cbb-qty-value]");
          const minusBtn = card.querySelector("[data-cbb-qty-minus]");
          const plusBtn = card.querySelector("[data-cbb-qty-plus]");
          const row = this.#selection.get(String(id));
          const q = row && Number.isFinite(row.quantity) && row.quantity > 0 ? row.quantity : 1;
          const alwaysQty = this.#qtySelectorAlwaysVisible();
          if (wrap instanceof HTMLElement) {
            wrap.hidden = !alwaysQty && !on;
          }
          if (valEl instanceof HTMLElement) {
            valEl.textContent = on ? String(q) : "0";
          }

          const qtyIdle = alwaysQty && !on;
          for (const b of [minusBtn, plusBtn]) {
            if (!(b instanceof HTMLButtonElement)) continue;
            b.disabled = Boolean(qtyIdle);
          }

          const imgEl = card.querySelector(".custom-bundle-builder__card-img");
          if (row && imgEl instanceof HTMLImageElement) {
            const u = (imgEl.currentSrc || imgEl.src || "").trim();
            if (u) row.imageUrl = u;
            const alt = (imgEl.alt || "").trim();
            if (alt) row.imageAlt = alt;
          }
        });
      }

      #updateSummary() {
        const roots = this.querySelectorAll("[data-cbb-summary-sync-root]");
        if (!roots.length) return;

        const selCount = this.#totalSelectedItemCount();
        let retail = 0;

        for (const entry of this.#selection.values()) {
          const q = Number.isFinite(entry.quantity) && entry.quantity > 0 ? entry.quantity : 1;
          retail += entry.priceCents * q;
        }

        const pct = this.#bestDiscountPercent(selCount);
        const bundlePrice = Math.round(retail * (1 - pct / 100));

        const strike = String(this.dataset.summaryStrikethrough || "") === "true";

        const headingText = `${this.#config.summaryTitle || ""} ${this.#buildSummaryCountText(selCount)}`.trim();
        const hintText = this.#getStepSummaryHint();
        const bundleDisplay = this.#formatMoneyCents(pct > 0 ? bundlePrice : retail);
        const retailDisplay = this.#formatMoneyCents(retail);
        const strikeOn = strike && pct > 0 && retail > bundlePrice;

        const leadRaw = (this.#config.summaryBundleLead || "").trim();
        const showLead = pct > 0 && leadRaw !== "" && retail > bundlePrice;

        roots.forEach((root) => {
          if (!(root instanceof HTMLElement)) return;

          const headingEl = root.querySelector("[data-cbb-summary-heading]");
          const hintEl = root.querySelector("[data-cbb-summary-hint]");
          const retailEl = root.querySelector("[data-cbb-retail]");
          const bundleEl = root.querySelector("[data-cbb-bundle-price]");
          const leadEl = root.querySelector("[data-cbb-bundle-lead]");

          if (!headingEl || !retailEl || !bundleEl) return;

          headingEl.textContent = headingText;

          if (hintEl instanceof HTMLElement) {
            hintEl.textContent = hintText;
            hintEl.hidden = hintText === "";
          }

          retailEl.textContent = retailDisplay;
          retailEl.classList.toggle("is-strike", strikeOn);

          bundleEl.textContent = bundleDisplay;

          if (leadEl instanceof HTMLElement) {
            if (showLead) {
              leadEl.textContent = leadRaw;
              leadEl.hidden = false;
            } else {
              leadEl.textContent = "";
              leadEl.hidden = true;
            }
          }
        });
      }

      #updateStepper() {
        const cur = this.#currentStep;

        this.querySelectorAll("[data-cbb-stepper-index]").forEach((el) => {
          const idx = parseInt(el.getAttribute("data-cbb-stepper-index") || "0", 10);
          const badge = el.querySelector(".custom-bundle-builder__stepper-badge");

          if (idx === cur) {
            el.dataset.cbbState = "active";
            if (badge) badge.classList.add("custom-bundle-builder__stepper-badge--active");
          } else if (idx < cur) {
            el.dataset.cbbState = this.#stepSatisfiesMin(idx) ? "completed" : "pending";
            if (badge) badge.classList.remove("custom-bundle-builder__stepper-badge--active");
          } else if (idx > cur && this.#stepperJumpAllowed(idx)) {
            el.dataset.cbbState = "next";
            if (badge) badge.classList.remove("custom-bundle-builder__stepper-badge--active");
          } else {
            el.dataset.cbbState = "pending";
            if (badge) badge.classList.remove("custom-bundle-builder__stepper-badge--active");
          }
        });

        this.querySelectorAll("[data-cbb-stepper-jump]").forEach((jumpEl) => {
          if (!(jumpEl instanceof HTMLElement)) return;
          const idx = parseInt(jumpEl.getAttribute("data-cbb-stepper-jump") || "0", 10);
          const canJump = this.#stepperJumpAllowed(idx);

          jumpEl.setAttribute("aria-disabled", canJump ? "false" : "true");
          jumpEl.setAttribute("tabindex", canJump ? "0" : "-1");
        });

        this.#applyStepperLayout();
      }

      /** Reorders stepper flex items when stacked layout is active (viewport <=989px). */
      #applyStepperLayout() {
        const mq =
          typeof window !== "undefined" && typeof window.matchMedia === "function"
            ? window.matchMedia("(max-width: 989px)")
            : null;

        const mobile = Boolean(mq && mq.matches);
        const grow = this.querySelector("[data-cbb-stepper-mobile-grow]");

        for (const el of this.querySelectorAll("[data-cbb-stepper-index]")) {
          if (!(el instanceof HTMLElement)) continue;
          const idx = parseInt(el.getAttribute("data-cbb-stepper-index") || "0", 10) || 0;

          if (!mobile) el.style.removeProperty("order");
          else if (String(el.dataset.cbbState || "") === "active") el.style.order = "-100";
          else el.style.order = String(idx);
        }

        if (!(grow instanceof HTMLElement)) return;

        if (!mobile) {
          grow.style.removeProperty("order");
          grow.style.display = "";
          grow.setAttribute("hidden", "");
          return;
        }

        grow.removeAttribute("hidden");
        grow.style.display = "";
        grow.style.order = "-50";
      }

      #updateCta() {
        const btn = this.querySelector("[data-cbb-primary-cta]");
        const label = btn?.querySelector("[data-cbb-cta-label]");
        if (!btn || !label) return;

        const total = this.#totalSteps();
        const isLast = this.#currentStep >= total;
        const visitedLast = this.#hasVisitedLastBuilderStep();
        const allMinsOk = this.#allStepsSatisfyMin();
        const canFinalize = visitedLast && allMinsOk;

        const showFinalizeLabel = visitedLast;

        const fallbackContinue = this.dataset.continueLabel || "Continue";
        let continueText = fallbackContinue;
        if (this.#continueUsesNextStepTitle() && !showFinalizeLabel) {
          const nextTitle = this.#nextStepTitleText();
          if (nextTitle !== "") continueText = nextTitle;
        }

        label.textContent = showFinalizeLabel ? this.dataset.finalizeLabel || "Create bundle" : continueText;

        if (!(btn instanceof HTMLElement)) return;

        if (!showFinalizeLabel && this.#continueUsesNextStepTitle() && continueText !== fallbackContinue) {
          btn.setAttribute("aria-label", `${fallbackContinue}: ${continueText}`);
        } else {
          btn.removeAttribute("aria-label");
        }

        let enabled;
        if (canFinalize) {
          enabled = true;
        } else if (!isLast) {
          enabled = this.#stepSatisfiesMin(this.#currentStep);
        } else {
          enabled = allMinsOk;
        }

        btn.toggleAttribute("disabled", !enabled);
      }

      #updateBackCta() {
        const btn = this.querySelector("[data-cbb-back-cta]");
        if (!(btn instanceof HTMLButtonElement)) return;
        btn.disabled = this.#currentStep <= 1;
      }
    }
  );
}